import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { DollarSign, ArrowDownLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { partnerApiService, PartnerWithdrawalHistoryItem } from '../../services/partnerApiService'
import { fmtUtcToKst } from '../../utils/dateKst'

const PartnerMyWithdrawals = () => {
  const { t, i18n } = useTranslation('withdrawals')
  const locale = i18n.language === 'ko' ? 'ko-KR' : i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'zh' ? 'zh-CN' : 'en-US'
  const [withdrawals, setWithdrawals] = useState<PartnerWithdrawalHistoryItem[]>([])
  const [balance, setBalance] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<PartnerWithdrawalHistoryItem | null>(null)
  
  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    account_info: '',
    account_type: 'BANK',
    memo: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // 파트너 잔고 로드
      const balanceResponse = await partnerApiService.getBalance()
      setBalance(balanceResponse.available_balance ?? 0)
      
      // 출금 내역 로드
      const withdrawalsResponse = await partnerApiService.getWithdrawalHistory()
      setWithdrawals(withdrawalsResponse || [])
    } catch (error) {
      console.error('데이터 로드 중 오류:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!withdrawForm.amount || !withdrawForm.account_info) {
      alert(t('myWithdrawals.modal.validationAmount'))
      return
    }

    const amount = parseFloat(withdrawForm.amount)
    if (amount <= 0 || amount > balance) {
      alert(t('myWithdrawals.modal.validationInvalidAmount'))
      return
    }
    // 백엔드 최소 출금액(10 USDT)과 동일 기준 사전 안내 (2026-07-24)
    if (amount < 10) {
      alert(t('myWithdrawals.modal.minAmount'))
      return
    }

    try {
      await partnerApiService.requestWithdrawal({
        amount: withdrawForm.amount,
        account_info: withdrawForm.account_info,
        account_type: withdrawForm.account_type,
        memo: withdrawForm.memo
      })
      
      alert(t('myWithdrawals.modal.success'))
      setShowWithdrawModal(false)
      setWithdrawForm({ amount: '', account_info: '', account_type: 'BANK', memo: '' })
      loadData()
    } catch (error: any) {
      console.error('출금 신청 중 오류:', error)
      // 백엔드가 주는 구체 사유(최소금액/가능액 초과/환율 장애 등)를 그대로 표시
      const detail = error?.response?.data?.detail
      alert(typeof detail === 'string' && detail ? detail : t('myWithdrawals.modal.error'))
    }
  }

  const getStatusColor = (status: string) => {
    const upperStatus = status.toUpperCase()
    switch (upperStatus) {
      case 'APPROVED': return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'REQUESTED': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      case 'PENDING': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      case 'REJECTED': return 'bg-red-500/20 text-red-400 border border-red-500/30'
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getStatusText = (status: string) => {
    const upperStatus = status.toUpperCase()
    switch (upperStatus) {
      case 'REQUESTED': return t('common:status.pendingWait')
      case 'PENDING': return t('common:status.pendingWait')
      case 'APPROVED': return t('common:status.approvedStatus')
      case 'REJECTED': return t('common:status.rejectedStatus')
      default: return status || t('common:status.unknown')
    }
  }

  // partner_withdrawals.created_at/processed_at 는 UTC 저장 → KST 로 변환 표시 (2026-10-06 사장님: 9시간 빠르게 보이던 문제)
  const formatDate = (dateString: string) => fmtUtcToKst(dateString, locale)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* 페이지 헤더 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-white/10 pb-4 sm:pb-6"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('myWithdrawals.title')}</h1>
            <p className="text-dark-400">{t('myWithdrawals.subtitle')}</p>
          </div>
          
          {/* 잔고 표시 */}
          <div className="text-right">
            <p className="text-sm text-dark-400">{t('myWithdrawals.currentBalance')}</p>
            <p className="text-xl sm:text-2xl font-bold text-green-400">
              {balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
            </p>
          </div>
        </div>
      </motion.div>

      {/* 출금 신청 버튼 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card p-4 sm:p-6"
      >
        <button
          onClick={() => setShowWithdrawModal(true)}
          className="btn-primary flex items-center space-x-2"
          disabled={balance < 10}
        >
          <DollarSign className="w-4 h-4" />
          <span>{t('myWithdrawals.withdrawButton')}</span>
        </button>
      </motion.div>

      {/* 출금 내역 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card"
      >
        <div className="p-4 sm:p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <ArrowDownLeft className="w-5 h-5 mr-2" />
              {t('myWithdrawals.withdrawalHistory')}
            </h3>
          </div>
        </div>

        {/* 데스크톱 테이블 */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('myWithdrawals.tableRequestDate')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('myWithdrawals.tableAmount')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('myWithdrawals.tableAccountInfo')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('myWithdrawals.tableAccountType')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider w-20">
                  {t('myWithdrawals.tableStatus')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('myWithdrawals.tableProcessDate')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('myWithdrawals.tableAction')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {withdrawals.map((withdrawal) => (
                <tr key={withdrawal.id} className="hover:bg-white/5 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {formatDate(withdrawal.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1 text-green-400" />
                      {parseFloat(withdrawal.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-white max-w-xs truncate">
                    {withdrawal.account_info}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {withdrawal.account_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap w-20">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border whitespace-nowrap ${getStatusColor(withdrawal.status)}`}>
                      {getStatusText(withdrawal.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-300">
                    {withdrawal.processed_at ? formatDate(withdrawal.processed_at) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => {
                        setSelectedWithdrawal(withdrawal)
                        setShowDetailModal(true)
                      }}
                      className="text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      {t('common:detail')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 모바일 카드 목록 */}
        <div className="lg:hidden divide-y divide-white/10">
          {withdrawals.map((withdrawal) => (
            <div key={withdrawal.id} className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center text-white font-medium mb-2">
                    <DollarSign className="w-4 h-4 mr-1 text-green-400" />
                    {parseFloat(withdrawal.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
                  </div>
                  <div className="text-sm text-dark-400">{formatDate(withdrawal.created_at)}</div>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(withdrawal.status)}`}>
                  {getStatusText(withdrawal.status)}
                </span>
              </div>
              
              <div className="space-y-3 mb-4 text-sm">
                <div>
                  <div className="text-dark-400 mb-1">{t('myWithdrawals.accountInfo')}</div>
                  <div className="text-white break-words">{withdrawal.account_info}</div>
                </div>
                <div>
                  <div className="text-dark-400 mb-1">{t('myWithdrawals.accountType')}</div>
                  <div className="text-white">{withdrawal.account_type}</div>
                </div>
                {withdrawal.processed_at && (
                  <div>
                    <div className="text-dark-400 mb-1">{t('myWithdrawals.processDate')}</div>
                    <div className="text-white">{formatDate(withdrawal.processed_at)}</div>
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  setSelectedWithdrawal(withdrawal)
                  setShowDetailModal(true)
                }}
                className="text-primary-400 hover:text-primary-300 transition-colors text-sm"
              >
                {t('common:detail')}
              </button>
            </div>
          ))}
        </div>

        {/* 빈 상태 */}
        {withdrawals.length === 0 && (
          <div className="p-8 sm:p-12 text-center">
            <ArrowDownLeft className="w-12 h-12 text-dark-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">{t('myWithdrawals.noWithdrawals')}</h3>
            <p className="text-dark-400">{t('myWithdrawals.noWithdrawalsYet')}</p>
          </div>
        )}
      </motion.div>

      {/* 출금 신청 모달 */}
      {showWithdrawModal && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-900 border border-white/10 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold mb-4 text-white">{t('myWithdrawals.modal.title')}</h3>

            <form onSubmit={handleWithdrawSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  {t('myWithdrawals.modal.amountLabel')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  max={balance}
                  value={withdrawForm.amount}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
                  placeholder={t('myWithdrawals.modal.amountPlaceholder')}
                  required
                />
                <p className="text-xs text-dark-400 mt-1">
                  {t('myWithdrawals.modal.maxWithdrawable', { amount: balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  {t('myWithdrawals.modal.accountTypeLabel')}
                </label>
                <select
                  value={withdrawForm.account_type}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, account_type: e.target.value })}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
                  required
                >
                  <option value="BANK">{t('myWithdrawals.modal.bankAccount')}</option>
                  <option value="CRYPTO">{t('myWithdrawals.modal.cryptoWallet')}</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  {t('myWithdrawals.modal.accountInfoLabel')}
                </label>
                <textarea
                  value={withdrawForm.account_info}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, account_info: e.target.value })}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
                  rows={3}
                  placeholder={t('myWithdrawals.modal.accountInfoPlaceholder')}
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  {t('myWithdrawals.modal.memoLabel')}
                </label>
                <textarea
                  value={withdrawForm.memo}
                  onChange={(e) => setWithdrawForm({ ...withdrawForm, memo: e.target.value })}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
                  rows={2}
                  placeholder={t('myWithdrawals.modal.memoPlaceholder')}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowWithdrawModal(false)
                    setWithdrawForm({ amount: '', account_info: '', account_type: 'BANK', memo: '' })
                  }}
                  className="flex-1 btn-secondary py-2.5"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary py-2.5"
                >
                  {t('common:submit')}
                </button>
              </div>
            </form>
          </motion.div>
        </div>,
        document.body
      )}

      {/* 상세보기 모달 */}
      {showDetailModal && selectedWithdrawal && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-dark-900 border border-white/10 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold mb-4 text-white">{t('myWithdrawals.detailModal.title')}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.requestId')}</label>
                <p className="text-sm text-white">{selectedWithdrawal.id}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.requestDate')}</label>
                <p className="text-sm text-white">{formatDate(selectedWithdrawal.created_at)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.amount')}</label>
                <p className="text-lg font-bold text-green-400">{parseFloat(selectedWithdrawal.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.accountType')}</label>
                <p className="text-sm text-white">{selectedWithdrawal.account_type}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.accountInfo')}</label>
                <p className="text-sm text-white whitespace-pre-wrap break-words">{selectedWithdrawal.account_info}</p>
              </div>
              
              {selectedWithdrawal.memo && (
                <div>
                  <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.memo')}</label>
                  <p className="text-sm text-white">{selectedWithdrawal.memo}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.status')}</label>
                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-lg ${getStatusColor(selectedWithdrawal.status)}`}>
                  {getStatusText(selectedWithdrawal.status)}
                </span>
              </div>
              
              {selectedWithdrawal.processed_at && (
                <div>
                  <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.processDate')}</label>
                  <p className="text-sm text-white">{formatDate(selectedWithdrawal.processed_at)}</p>
                </div>
              )}
              
              {selectedWithdrawal.admin_memo && (
                <div>
                  <label className="block text-sm font-medium text-dark-400 mb-1">{t('myWithdrawals.detailModal.adminMemo')}</label>
                  <div className="glass-dark p-3 rounded-lg">
                    <p className="text-sm text-white">{selectedWithdrawal.admin_memo}</p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="btn-secondary"
              >
                {t('common:close')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default PartnerMyWithdrawals